import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Brain, Users, CalendarDays, Plane, LayoutGrid, ShieldCheck,
  Zap, RefreshCw, BookOpen, Scale, FileText, MapPin,
  ChevronRight, ChevronLeft, Play, Pause, X, ArrowUpRight, Home,
} from "lucide-react";

// ─── 12 Fatigue Module Spokes ──────────────────────────────────────────────────
const SPOKES = [
  {
    id: "frms", label: "FRMS",
    fullName: "Fatigue Risk Management System",
    Icon: Brain, color: "#a78bfa",
    stat: "72h", statLabel: "Prediction Horizon",
    headline: "Predict fatigue before the flight is assigned.",
    desc: "Biomathematical SAFTE-FAST scoring models every crew member's alertness 72 hours ahead. The system knows who is safe before the roster is ever published.",
    link: "/frms",
  },
  {
    id: "crew-master", label: "Crew Master",
    fullName: "Crew Master",
    Icon: Users, color: "#60a5fa",
    stat: "350+", statLabel: "Crew Profiles",
    headline: "Every crew member. One source of truth.",
    desc: "Qualifications, medicals, leave balances, fatigue history and base positions — one master database driving every scheduling and compliance decision.",
    link: "/roster/crew",
  },
  {
    id: "roster", label: "Roster Planner",
    fullName: "Roster Planner",
    Icon: CalendarDays, color: "#34d399",
    stat: "0", statLabel: "Manual FDTL Checks",
    headline: "Rosters built around rest, not convenience.",
    desc: "AI-generated duty schedules where every assignment is pre-checked against FDTL limits. No crew reaches the roster in violation — the system prevents it upstream.",
    link: "/roster/planner",
  },
  {
    id: "pairings", label: "Pairing Builder",
    fullName: "Pairing Builder",
    Icon: Plane, color: "#38bdf8",
    stat: "13h/10h", statLabel: "Day / WOCL FDP Limits",
    headline: "Every FDP engineered to the hour.",
    desc: "Flight duty periods constructed within DGCA Nov 2025 limits. Day cap 13h. WOCL cap 10h. Split duty, augmented crew, and extension rules applied automatically.",
    link: "/roster/pairings",
  },
  {
    id: "monthly", label: "Monthly Scheduler",
    fullName: "Monthly Scheduler",
    Icon: LayoutGrid, color: "#fb923c",
    stat: "30-day", statLabel: "Planning Horizon",
    headline: "One month. Every conflict found.",
    desc: "Full-month crew horizon with leave, training, simulator slots and peak demand all conflict-checked before publication. No surprises mid-month.",
    link: "/roster/monthly-scheduler",
  },
  {
    id: "compliance", label: "Compliance Board",
    fullName: "DGCA Compliance Board",
    Icon: ShieldCheck, color: "#f87171",
    stat: "12", statLabel: "DGCA Nov 2025 Rules",
    headline: "Zero manual checks. Every rule enforced.",
    desc: "12 DGCA CAR-OPS November 2025 rules enforced in real time. FDP limits, weekly rest, night-duty caps, monthly block hours — all flagged before the inspector.",
    link: "/roster/compliance",
  },
  {
    id: "simulator", label: "AI Simulator",
    fullName: "Scenario Simulator",
    Icon: Zap, color: "#fbbf24",
    stat: "AI", statLabel: "Claude-Powered",
    headline: "Run the crisis before it runs you.",
    desc: "Claude AI models any disruption — late aircraft, crew sick-off, weather diversion — and returns a fully compliant recovery plan with fatigue scores in seconds.",
    link: "/roster/simulator",
  },
  {
    id: "recovery", label: "IROPS Recovery",
    fullName: "IROPS Recovery Console",
    Icon: RefreshCw, color: "#f97316",
    stat: "< 60s", statLabel: "Crew Reassignment",
    headline: "Disruption resolved without a fatigue breach.",
    desc: "Flight cancelled? Aircraft swapped? The system reassigns crew within seconds, respecting rest periods, WOCL limits and qualifications. No phone calls needed.",
    link: "/roster/recovery",
  },
  {
    id: "training", label: "Training Engine",
    fullName: "Training Engine",
    Icon: BookOpen, color: "#2dd4bf",
    stat: "0", statLabel: "Post-WOCL Sim Sessions",
    headline: "Training scheduled around fatigue, not around slots.",
    desc: "Simulator sessions, type-rating renewals and recurrency checks are fatigue-scored before scheduling. No crew walks into a simulator after a WOCL night turn.",
    link: "/training-engine",
  },
  {
    id: "fairness", label: "Fairness Engine",
    fullName: "Fairness Engine",
    Icon: Scale, color: "#c084fc",
    stat: "Equitable", statLabel: "Duty Distribution",
    headline: "No captain carries three times the night duties.",
    desc: "Equity scoring ensures night duties, long pairings and weekend shifts are distributed proportionally across the crew pool. Fair rosters improve retention.",
    link: "/fairness-engine",
  },
  {
    id: "audit", label: "Audit Log",
    fullName: "Immutable Audit Log",
    Icon: FileText, color: "#94a3b8",
    stat: "100%", statLabel: "DGCA Traceable",
    headline: "Every decision. Permanent record.",
    desc: "Every scheduling action, compliance override and crew change is logged immutably with timestamp, user and reason. DGCA inspection-ready at any moment.",
    link: "/roster/audit",
  },
  {
    id: "deployment", label: "Crew Deployment",
    fullName: "Crew Deployment Tracker",
    Icon: MapPin, color: "#4ade80",
    stat: "Live", statLabel: "Station Positions",
    headline: "Know where every crew member is, right now.",
    desc: "Real-time crew positioning across all stations — who is at base, in transit, on duty, or available for emergency cover. One view replaces a dozen WhatsApp groups.",
    link: "/roster/deployment",
  },
];

// ─── Spoke geometry helper — 12 nodes, 30° apart, starting at top ─────────────
function getSpokeStyle(index, radiusPct) {
  const angle = ((index * 30) - 90) * (Math.PI / 180);
  const x = Math.cos(angle) * radiusPct;
  const y = Math.sin(angle) * radiusPct;
  return {
    position: "absolute",
    left: `calc(50% + ${x}%)`,
    top: `calc(50% + ${y}%)`,
    transform: "translate(-50%, -50%)",
  };
}

// SVG line endpoint for a given spoke index, within a 1000×1000 viewBox
function getSvgPoint(index, radius) {
  const angle = ((index * 30) - 90) * (Math.PI / 180);
  return {
    x: 500 + Math.cos(angle) * radius,
    y: 500 + Math.sin(angle) * radius,
  };
}

export default function FatigueTheatre() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("intro"); // intro | hub | interactive
  const [visibleCount, setVisibleCount] = useState(0);
  const [activeIdx, setActiveIdx] = useState(null);
  const [autoPlay, setAutoPlay] = useState(false);
  const autoRef = useRef(null);

  // ── Phase: intro → hub ─────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "intro") return;
    const t = setTimeout(() => setPhase("hub"), 3400);
    return () => clearTimeout(t);
  }, [phase]);

  // ── Phase: hub — animate spokes out one by one ────────────────────────────
  useEffect(() => {
    if (phase !== "hub") return;
    let count = 0;
    const iv = setInterval(() => {
      count += 1;
      setVisibleCount(count);
      if (count >= SPOKES.length) {
        clearInterval(iv);
        setTimeout(() => setPhase("interactive"), 500);
      }
    }, 260);
    return () => clearInterval(iv);
  }, [phase]);

  // ── Auto-play ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!autoPlay || phase !== "interactive") return;
    const start = activeIdx === null ? 0 : (activeIdx + 1) % SPOKES.length;
    setActiveIdx(start);
    autoRef.current = setInterval(() => {
      setActiveIdx(prev => (prev === null ? 0 : (prev + 1) % SPOKES.length));
    }, 4800);
    return () => clearInterval(autoRef.current);
  }, [autoPlay, phase]); // eslint-disable-line

  const toggleAuto = () => {
    if (autoPlay) { clearInterval(autoRef.current); setAutoPlay(false); }
    else setAutoPlay(true);
  };

  const goNext = useCallback(() => {
    setAutoPlay(false);
    setActiveIdx(p => p === null ? 0 : (p + 1) % SPOKES.length);
  }, []);

  const goPrev = useCallback(() => {
    setAutoPlay(false);
    setActiveIdx(p => p === null ? SPOKES.length - 1 : (p - 1 + SPOKES.length) % SPOKES.length);
  }, []);

  const activeSpoke = activeIdx !== null ? SPOKES[activeIdx] : null;
  const isHub = phase === "hub" || phase === "interactive";

  return (
    <div
      className="fixed inset-0 overflow-hidden select-none"
      style={{ background: "radial-gradient(ellipse at 50% 40%, #0e1628 0%, #060a12 100%)" }}
      data-testid="fatigue-theatre"
    >
      {/* Subtle grid */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.035]"
        style={{ backgroundImage: "linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)", backgroundSize: "56px 56px" }} />

      {/* Home / Exit */}
      <button onClick={() => navigate("/")} className="absolute top-5 left-6 z-50 flex items-center gap-1.5 text-[11px] text-white/35 hover:text-white/70 transition-colors">
        <Home size={12} /> Home
      </button>
      <button onClick={() => navigate("/dashboard")} className="absolute top-5 right-6 z-50 flex items-center gap-1.5 text-[11px] text-white/35 hover:text-white/70 transition-colors">
        <X size={12} /> Exit
      </button>

      {/* ── INTRO ──────────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {phase === "intro" && (
          <motion.div key="intro"
            className="absolute inset-0 flex flex-col items-center justify-center z-40 pointer-events-none"
            exit={{ opacity: 0, transition: { duration: 0.7 } }}>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}
              className="text-[11px] tracking-[0.45em] uppercase text-blue-400/60 mb-5 font-mono">
              Scenario Theatre — Fatigue Shield
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }}
              className="text-[clamp(4rem,9vw,8rem)] font-black tracking-tight text-white leading-none text-center mb-4">
              FATIGUE<br /><span style={{ color: "#60a5fa" }}>SHIELD</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 0.9 }}
              className="text-base text-white/45 text-center max-w-md leading-relaxed">
              Bringing safety without increasing crew or staff cost.<br />
              <span className="text-white/75 font-medium">Just better AI management.</span>
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── HUB + SPOKES ───────────────────────────────────────────────────── */}
      {isHub && (
        <motion.div
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          {/* SVG lines layer */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1000 1000" preserveAspectRatio="xMidYMid meet">
            <defs>
              <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
              </radialGradient>
              <filter id="blur-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="6" result="b" />
                <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>

            {/* Center glow circle */}
            <circle cx="500" cy="500" r="80" fill="url(#centerGlow)" />

            {/* Spoke lines */}
            {SPOKES.map((s, i) => {
              if (i >= visibleCount) return null;
              const pt = getSvgPoint(i, 310);
              const isActive = activeIdx === i;
              return (
                <motion.line
                  key={s.id}
                  x1="500" y1="500" x2={pt.x} y2={pt.y}
                  stroke={isActive ? s.color : "rgba(255,255,255,0.13)"}
                  strokeWidth={isActive ? 2.5 : 1.2}
                  initial={{ opacity: 0, scaleX: 0 }}
                  animate={{ opacity: 1, scaleX: 1 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  style={{ transformOrigin: "500px 500px" }}
                />
              );
            })}

            {/* Outer ring */}
            {phase === "interactive" && (
              <motion.circle cx="500" cy="500" r="310"
                fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} />
            )}
          </svg>

          {/* Centre Core */}
          <motion.div
            className="absolute"
            style={{
              left: "50%", top: "50%",
              width: "clamp(96px,8.5vmin,128px)",
              height: "clamp(96px,8.5vmin,128px)",
              transform: "translate(-50%,-50%)",
              borderRadius: "50%",
              border: "1px solid rgba(96,165,250,0.35)",
              background: "radial-gradient(circle, rgba(59,130,246,0.2) 0%, rgba(59,130,246,0.03) 70%, transparent 100%)",
              boxShadow: "0 0 48px rgba(59,130,246,0.22), inset 0 0 24px rgba(59,130,246,0.08)",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              zIndex: 20,
            }}
            animate={{ boxShadow: ["0 0 48px rgba(59,130,246,0.22)", "0 0 72px rgba(59,130,246,0.38)", "0 0 48px rgba(59,130,246,0.22)"] }}
            transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
          >
            <div className="text-[9px] font-mono tracking-[0.2em] uppercase text-blue-300/50">Fatigue</div>
            <div className="text-xs font-black tracking-widest text-white mt-0.5">SHIELD</div>
            <div className="text-[8px] font-mono tracking-[0.2em] uppercase text-blue-300/40 mt-0.5">AI Core</div>
          </motion.div>

          {/* Spoke Nodes */}
          {SPOKES.map((s, i) => {
            if (i >= visibleCount) return null;
            const isActive = activeIdx === i;
            const isDimmed = activeIdx !== null && !isActive;
            const spokeStyle = getSpokeStyle(i, 31); // 31% of container size

            return (
              <motion.button
                key={s.id}
                data-testid={`spoke-${s.id}`}
                style={{ ...spokeStyle, zIndex: 30 }}
                className="flex flex-col items-center group"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: isDimmed ? 0.3 : 1 }}
                transition={{ type: "spring", stiffness: 220, damping: 20, delay: i * 0.05 }}
                whileHover={{ scale: 1.12 }}
                onClick={() => { setAutoPlay(false); setActiveIdx(isActive ? null : i); }}
              >
                {/* Icon circle */}
                <div
                  style={{
                    width: "clamp(40px,3.8vmin,56px)",
                    height: "clamp(40px,3.8vmin,56px)",
                    borderRadius: "50%",
                    background: isActive
                      ? `radial-gradient(circle, ${s.color}30 0%, ${s.color}08 100%)`
                      : "rgba(255,255,255,0.05)",
                    border: `1.5px solid ${isActive ? s.color : "rgba(255,255,255,0.15)"}`,
                    boxShadow: isActive ? `0 0 20px ${s.color}50, 0 0 6px ${s.color}30` : "none",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.3s ease",
                  }}
                >
                  <s.Icon size={15} color={isActive ? s.color : "rgba(255,255,255,0.55)"} />
                </div>
                {/* Label */}
                <span
                  style={{
                    marginTop: 6,
                    fontSize: "clamp(8px,0.85vmin,10px)",
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    color: isActive ? s.color : "rgba(255,255,255,0.45)",
                    whiteSpace: "nowrap",
                    transition: "color 0.3s ease",
                  }}
                >
                  {s.label}
                </span>
              </motion.button>
            );
          })}
        </motion.div>
      )}

      {/* ── DETAIL PANEL ──────────────────────────────────────────────────── */}
      <AnimatePresence>
        {activeSpoke && phase === "interactive" && (
          <motion.div
            key={activeSpoke.id}
            className="absolute right-0 top-0 bottom-0 z-40 overflow-hidden"
            style={{
              width: "clamp(300px,28vw,420px)",
              background: "linear-gradient(to right, rgba(6,10,18,0.97) 0%, rgba(6,10,18,0.92) 100%)",
              borderLeft: `1px solid ${activeSpoke.color}22`,
            }}
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {/* Top color bar */}
            <div style={{ height: 3, background: activeSpoke.color, opacity: 0.7 }} />

            <div className="flex flex-col h-full px-8 py-10 overflow-y-auto">
              {/* Module counter */}
              <div className="text-[10px] font-mono tracking-[0.35em] uppercase mb-6"
                style={{ color: activeSpoke.color + "aa" }}>
                Module {String(activeIdx + 1).padStart(2, "0")} of 12
              </div>

              {/* Icon + Name */}
              <div className="flex items-center gap-3 mb-6">
                <div style={{
                  width: 44, height: 44, borderRadius: 6,
                  background: activeSpoke.color + "18",
                  border: `1px solid ${activeSpoke.color}33`,
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <activeSpoke.Icon size={22} color={activeSpoke.color} />
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-white/35 font-mono">Fatigue Layer</div>
                  <div className="text-sm font-bold text-white leading-tight mt-0.5">{activeSpoke.fullName}</div>
                </div>
              </div>

              {/* Giant stat */}
              <motion.div key={activeSpoke.id + "s"}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <div className="font-black leading-none mb-1"
                  style={{ fontSize: "clamp(2.8rem,5.5vw,5rem)", color: activeSpoke.color }}>
                  {activeSpoke.stat}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-white/35 font-mono mb-6">
                  {activeSpoke.statLabel}
                </div>
              </motion.div>

              {/* Headline */}
              <motion.h3 key={activeSpoke.id + "h"}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
                className="text-base font-bold text-white leading-snug mb-3">
                {activeSpoke.headline}
              </motion.h3>

              {/* Description */}
              <motion.p key={activeSpoke.id + "d"}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.26 }}
                className="text-sm text-white/50 leading-relaxed mb-8">
                {activeSpoke.desc}
              </motion.p>

              {/* CTA */}
              <motion.button
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.36 }}
                onClick={() => navigate(activeSpoke.link)}
                className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest px-4 py-2.5 rounded-sm self-start transition-all hover:opacity-80"
                style={{ border: `1px solid ${activeSpoke.color}44`, color: activeSpoke.color, background: activeSpoke.color + "15" }}>
                Open Live Module <ArrowUpRight size={13} />
              </motion.button>

              {/* Progress dots */}
              <div className="flex gap-1.5 mt-8 flex-wrap">
                {SPOKES.map((_, idx) => (
                  <button key={idx} onClick={() => setActiveIdx(idx)}
                    style={{
                      height: 4, borderRadius: 2,
                      width: idx === activeIdx ? 18 : 5,
                      background: idx === activeIdx ? activeSpoke.color : "rgba(255,255,255,0.15)",
                      transition: "all 0.3s ease",
                    }} />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── TAGLINE FOOTER ────────────────────────────────────────────────── */}
      <AnimatePresence>
        {phase === "interactive" && (
          <motion.div key="tagline"
            className="absolute bottom-14 left-0 right-0 text-center pointer-events-none z-20"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 1 }}>
            <p className="text-[10px] text-white/22 tracking-[0.22em] uppercase font-mono">
              Bringing safety without increasing crew or staff cost &mdash; just better AI management
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── BOTTOM NAV ────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {phase === "interactive" && (
          <motion.nav key="nav"
            className="absolute bottom-0 left-0 right-0 z-50 flex items-center justify-center gap-3 px-6 py-2.5"
            style={{ background: "rgba(6,10,18,0.88)", borderTop: "1px solid rgba(255,255,255,0.06)", backdropFilter: "blur(12px)" }}
            initial={{ y: 56, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, type: "spring", stiffness: 220 }}>

            <button onClick={goPrev} data-testid="theatre-prev"
              className="flex items-center gap-1 text-[11px] text-white/45 hover:text-white/80 transition-colors px-3 py-1.5 border border-white/10 rounded-sm hover:border-white/20">
              <ChevronLeft size={12} /> Prev
            </button>

            <div className="flex-1 text-center">
              {activeSpoke ? (
                <span className="text-[10px] font-mono tracking-widest uppercase" style={{ color: activeSpoke.color }}>
                  {activeIdx + 1} / 12 — {activeSpoke.fullName}
                </span>
              ) : (
                <span className="text-[10px] text-white/28 font-mono tracking-widest uppercase">
                  Click any node or press Auto-Play
                </span>
              )}
            </div>

            <button onClick={toggleAuto} data-testid="theatre-autoplay"
              className="flex items-center gap-1.5 text-[11px] px-4 py-1.5 rounded-sm transition-all border"
              style={{
                color: autoPlay ? "#60a5fa" : "rgba(255,255,255,0.45)",
                borderColor: autoPlay ? "rgba(96,165,250,0.4)" : "rgba(255,255,255,0.1)",
                background: autoPlay ? "rgba(96,165,250,0.09)" : "transparent",
              }}>
              {autoPlay ? <Pause size={12} /> : <Play size={12} />}
              {autoPlay ? "Pause" : "Auto-Play"}
            </button>

            <button onClick={goNext} data-testid="theatre-next"
              className="flex items-center gap-1 text-[11px] text-white/45 hover:text-white/80 transition-colors px-3 py-1.5 border border-white/10 rounded-sm hover:border-white/20">
              Next <ChevronRight size={12} />
            </button>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* Loading counter during hub phase */}
      {phase === "hub" && (
        <div className="absolute bottom-8 left-0 right-0 text-center pointer-events-none z-40">
          <span className="text-[10px] font-mono text-white/25 tracking-widest">
            Loading fatigue layers&nbsp;
            <span style={{ color: "#60a5fa" }}>{visibleCount}</span>
            &nbsp;/&nbsp;{SPOKES.length}
          </span>
        </div>
      )}
    </div>
  );
}
